import React, { useMemo, useState } from 'react'
import moment from 'moment'
import { Calendar, momentLocalizer } from 'react-big-calendar'
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import 'moment/locale/pt-br'
import { Form } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCalendar } from '@fortawesome/free-solid-svg-icons'
import { trans } from 'sharedKernel/i18n'
import { GetFormat, useGet } from 'sharedKernel/api'
import { PageTitle } from 'domain/feature/hub/pageTitle/styles'
import {
  CalendarWrapper,
  CustomCalendarGlobalStyle,
  ToolbarRow,
  ToolbarNav,
  ToolbarLabel,
  ToolbarSearch,
  ToolbarViews,
} from './style'

const localizer = momentLocalizer(moment)
interface Event {
  id: string
  title: string
  start: Date
  end: Date
  desc: string
  color: string
}
interface ChallengeEvent extends Event {
  type: 'challenge'
}
interface EventEvent extends Event {
  type: 'event'
}
type CalendarEvent = ChallengeEvent | EventEvent

interface CustomToolbarProps {
  label: string
  onNavigate: (action: 'PREV' | 'NEXT' | 'TODAY' | Date) => void
  onView: (view: string) => void
  view: string
  views: string[]
  searchTerm: string
  setSearchTerm: (value: string) => void
}

const CustomToolbar: React.FC<CustomToolbarProps> = (props) => {
  const { label, onNavigate, onView, view, views, searchTerm, setSearchTerm } = props
  const [searchValue, setSearchValue] = useState(searchTerm)

  React.useEffect(() => {
    setSearchValue(searchTerm)
  }, [searchTerm])

  return (
    <div className="rbc-toolbar" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <ToolbarRow>
        <ToolbarNav className="rbc-btn-group">
          <button type="button" className="calendar-today-btn" onClick={() => onNavigate('TODAY')}>Hoje</button>
          <button type="button" onClick={() => onNavigate('PREV')}>&lt;</button>
          <button type="button" onClick={() => onNavigate('NEXT')}>&gt;</button>
          <ToolbarLabel className="rbc-toolbar-label">
            <FontAwesomeIcon icon={faCalendar} style={{ marginRight: 8 }} />
            {label.charAt(0).toUpperCase() + label.slice(1)}
          </ToolbarLabel>
        </ToolbarNav>
        <ToolbarSearch>
          <Form.Group className="rbc-toolbar" style={{ marginBottom: 0 }}>
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder={trans('Pesquisar...')}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onBlur={() => setSearchTerm(searchValue)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchTerm(searchValue)
                  }
                }}
                style={{ width: '150px', height: '32px' }}
              />
            </div>
          </Form.Group>
        </ToolbarSearch>
        <ToolbarViews className="rbc-btn-group">
          {views.map(v => (
            <button
              type="button"
              key={v}
              className={`calendar-view-btn${view === v ? ' rbc-active' : ''}`}
              onClick={() => onView(v)}
            >
              {v === 'month' ? 'Mês' : v === 'week' ? 'Semana' : v === 'day' ? 'Dia' : v}
            </button>
          ))}
        </ToolbarViews>
      </ToolbarRow>
    </div>
  )
}

const CustomWeekHeader = ({ label, date }: { label: string, date: Date }): JSX.Element => {
  const dayName = `${label.charAt(0).toUpperCase()}${label.slice(1)}`
  const dayNumber = `${(date.getDate() < 10 ? '0' : '')}${date.getDate()}`
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.1 }}>
      <span style={{ color: '#0066B3', fontWeight: 500 }}>{dayName}</span>
      <span style={{ color: '#5A6970', fontWeight: 500, fontSize: 18 }}>{dayNumber}</span>
    </div>
  )
}

const CustomEvent: React.FC<{ event: CalendarEvent }> = ({ event }) => {
  if ('type' in event && event.type === 'event') {
    const startHour = moment(event.start).format('HH')
    const endHour = moment(event.end).format('HH')
    let timeString = ''
    if (startHour !== '' && endHour !== '' && startHour !== endHour) {
      timeString = `${startHour}h às ${endHour}h - `
    } else if (startHour !== '') {
      timeString = `${startHour}h - `
    }
    return (
      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', display: 'block' }}>
        {timeString}{event.title}
      </span>
    )
  }
  return <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%', display: 'block' }}>{event.title}</span>
}

export const CustomCalendar = ({ challengeList }): JSX.Element => {
  const navigate = useNavigate()
  const DragAndDropCalendar = withDragAndDrop(Calendar)
  const [searchTerm, setSearchTerm] = useState('')
  const request: GetFormat = {
    url: 'v1/hub/company/events?page=1'
  }
  const { data, isFetching, refetch } = useGet<Event[]>(request, 'events')
  const { defaultDate, views } = useMemo(
    () => ({
      defaultDate: moment.now(),
      views: {
        month: true,
        week: true,
        day: true
      }
    }), []
  )

  const eventStyle = (event): any => ({
    style: {
      backgroundColor: event.color
    }
  })

  const calendarEvents: CalendarEvent[] = [

    ...(Array.isArray(challengeList) ? challengeList : [])
      .filter(data =>
        data.config.title.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .map(data => {
        const challenge: ChallengeEvent = {
          id: data.id,
          title: data.config.title,
          start: new Date(data.config.step.applicationStartDate),
          end: new Date(data.config.step.applicationStartDate),
          desc: data.style.shortDescription,
          color: '#FF7A00',
          type: 'challenge'
        }
        return challenge
      }),

    ...((Array.isArray(data?.rows) ? data.rows.filter(event => event.isLaunched) : [])
      .filter(event =>
        event.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
      .flatMap(event => {
        if (
          event.startDate != null && event.startTime != null &&
          event.endDate != null && event.endTime != null
        ) {
          const startDate = event.startDate.split('T')[0]
          const endDate = event.endDate.split('T')[0]
          const startTime = event.startTime
          const endTime = event.endTime
          const start = new Date(`${startDate}T${startTime}`)
          const end = new Date(`${endDate}T${endTime}`)

          if (startDate === endDate) {
            const eventObj: EventEvent = {
              id: event.id,
              title: event.name,
              start,
              end,
              desc: event.description,
              color: '#a89be7',
              type: 'event'
            }
            return [eventObj]
          }

          const events: EventEvent[] = []
          const days = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1
          for (let i = 0; i < days; i++) {
            const currentDate = new Date(start)
            currentDate.setDate(start.getDate() + i)
            const currentDateStr = currentDate.toISOString().split('T')[0]
            const eventStart = new Date(`${currentDateStr}T${startTime}`)
            const eventEnd = new Date(`${currentDateStr}T${endTime}`)
            const eventObj: EventEvent = {
              id: event.id,
              title: event.name,
              start: eventStart,
              end: eventEnd,
              desc: event.description,
              color: '#a89be7',
              type: 'event'
            }
            events.push(eventObj)
          }
          return events
        } else {
          const eventObj: EventEvent = {
            id: event.id,
            title: event.name,
            start: new Date(event.startDate ?? ''),
            end: new Date(event.endDate ?? ''),
            desc: event.description,
            color: '#a89be7',
            type: 'event'
          }
          return [eventObj]
        }
      })
    )
  ]
  const handleEventClick = (e: any): void => {
    if (e.type === 'challenge') {
      navigate(`/hub/company/challenge/${e.id}`)
    } else if (e.type === 'event') {
      navigate(`/hub/company/event/${e.id}`)
    }
  }

  return (
    <div className='tela'>
      <div>
        <PageTitle style={{ textAlign: 'left', width: '100%' }}>Calendário</PageTitle>
      </div>
      <div className='calendario'>
        <CustomCalendarGlobalStyle />
        <CalendarWrapper>
          <DragAndDropCalendar
            defaultView='month'
            events={calendarEvents}
            defaultDate={defaultDate}
            localizer={localizer}
            resizable
            messages={{
              next: '>',
              previous: '<',
              today: 'Hoje',
              month: 'Mês',
              week: 'Semana',
              day: 'Dia',
              showMore: (total) => `+${total}`
            }}
            culture='pt-br'
            views={views}
            className='calendar'
            eventPropGetter={eventStyle}
            onSelectEvent={handleEventClick}
            formats={{
              weekdayFormat: (date, culture, localizer) => {
                const label = localizer.format(date, 'ddd', culture)
                return `${label.charAt(0).toUpperCase()}${label.slice(1)}`
                },
              popupHeaderFormat: (date, culture, localizer) => {
                const dayName = localizer.format(date, 'dddd', culture)
                const dayNumber = localizer.format(date, 'D', culture)
                const monthName = localizer.format(date, 'MMM', culture)
                return `${dayName} ${dayNumber} ${monthName}`
              }
            }}
            components={{
              event: CustomEvent,
              toolbar: (props: any) => (
                <CustomToolbar
                  {...props}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                />
              ),
              dateHeader: ({ label, date, drilldownView, ...rest }) => {
                if (rest?.view === 'week') {
                  return <CustomWeekHeader label={label} date={date} />
                }
                return label
              }
            }}
            popup
            popupOffset={{ x: 0, y: 20 }}
            longPressThreshold={10}
            dayLayoutAlgorithm="no-overlap"
            minRows={2}
          />
        </CalendarWrapper>
      </div>
    </div>
  )
}
